/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import ChevronIcon from 'Component/ChevronIcon';
import { BOTTOM, TOP } from 'Component/ChevronIcon/ChevronIcon.config';
import ClickOutside from 'Component/ClickOutside';
import { BLACK_KEY, M_KEY } from 'Component/Icon/Icon.config';
import { EventsType, FieldAttrType, FieldOptionsType } from 'Type/Field.type';
import isMobile from 'Util/Mobile';
import { HEIGHT_LARGE, HEIGHT_SMALL } from 'Util/Validator/Field.config';

import {
 FIX_SIZE, FULL_SIZE
} from './FieldSelect.config';

/**
 * Field Select
 * @class FieldSelect
 * @namespace Bodypwa/Component/FieldSelect/Component */
export class FieldSelectComponent extends PureComponent {
    static propTypes = {
        attr: FieldAttrType.isRequired,
        events: EventsType.isRequired,
        options: FieldOptionsType.isRequired,
        setRef: PropTypes.func.isRequired,
        isExpanded: PropTypes.bool.isRequired,
        handleSelectListOptionClick: PropTypes.func.isRequired,
        handleSelectListKeyPress: PropTypes.func.isRequired,
        handleSelectExpandedExpand: PropTypes.func.isRequired,
        handleSelectExpand: PropTypes.func.isRequired,
        isDisabled: PropTypes.bool.isRequired,
        size: PropTypes.oneOf([FIX_SIZE, FULL_SIZE]),
        isReadOnly: PropTypes.bool,
        heightSize: PropTypes.oneOf([HEIGHT_SMALL, HEIGHT_LARGE])
    };

    static defaultProps = {
        size: FULL_SIZE,
        isReadOnly: false,
        heightSize: HEIGHT_LARGE
    };

    sizeClass = {
        FULL_SIZE: 'w-full',
        FIX_SIZE: 'w-300'
    };

    heightClass = {
        [HEIGHT_SMALL]: 'py-2.5 pl-4',
        [HEIGHT_LARGE]: 'p-4'
    };

    renderNativeOption = (option) => {
        const {
            id,
            value,
            disabled,
            label,
            subLabel = '',
            isPlaceholder
        } = option;

        return (
            <option
                key={ id }
                id={ id }
                value={ value }
                disabled={ disabled && !isPlaceholder }
            >
                { `${label} ${subLabel}` }
            </option>
        );
    };

    renderNativeSelect() {
        const {
            setRef, attr, events, isDisabled, options, handleSelectListOptionClick, isReadOnly, isExpanded, heightSize
        } = this.props;
        const filteredOptions = options.length === 2 ? options.filter((option) => !option.isPlaceholder) : options;
        const shouldApplyHover = !isReadOnly && !isDisabled;
        const expandedClass = isExpanded ? 
            'shadow-[inset_0_0_0_1px_var(--color-neutral-1000)] border-neutral-1000' : '';

        const isHover = shouldApplyHover ? 
            `group-hover:shadow-[inset_0_0_0_1px_var(--color-neutral-1000)] 
            group-active:shadow-[inset_0_0_0_1px_var(--color-neutral-1000)] group-hover:border-neutral-1000` : '';

        const classNames = isReadOnly ? 'bg-bmBg-muted border-none cursor-not-allowed pointer-events-none' : '';
        const classDisabled = isDisabled ? 
            `disabled:pointer-events-none disabled:bg-bmBg-muted disabled:text-bmText-inverted-muted 
            disabled:border-bmBorder-disabled` : '';

        return (
            <select
                className={ `lg:pointer-events-none text-p16 leading-5.5
              ${ classNames } ${classDisabled} ${this.heightClass[heightSize]} ${isHover} ${expandedClass}` }
                ref={ (elem) => setRef(elem) }
                disabled={ isDisabled }
                { ...attr }
                { ...events }
                onChange={ handleSelectListOptionClick }
            >
                { filteredOptions.map(this.renderNativeOption) }
            </select>
        );
    }

    renderOption = (option) => {
        const {
            id,
            label,
            subLabel,
            isPlaceholder = false
        } = option;

        const {
            isExpanded,
            handleSelectListOptionClick
        } = this.props;

        return (
            <li
                className={ `p-0 m-0 z-10 transition-[100ms] duration-max-h ease-in will-change-[max-height] max-h-14
              bg-neutral-0 text-p16 p-4 hover:bg-bmBg-brand active:bg-bmBg-brand focus:bg-bmBg-brand
              ${isPlaceholder}` }
                key={ id }
                id={ `o${id}` }
                role="menuitem"
                onMouseDown={ () => handleSelectListOptionClick(option) }
                onTouchStart={ () => handleSelectListOptionClick(option) }
                onKeyPress={ () => handleSelectListOptionClick(option) }
                tabIndex={ isExpanded ? '0' : '-1' }
            >
                { label }
                { subLabel && (
                    <strong>
                        { ` ${subLabel}` }
                    </strong>
                ) }
            </li>
        );
    };

    renderOptions() {
        const {
            options,
            isExpanded,
            size
        } = this.props;
        const classIsExpanded = isExpanded ? 
            `lg:z-15 lg:max-h-[200px] lg:overflow-y-auto lg:bg-neutral-0 lg:border-t-none lg:border-x-2 lg:border-b-2 
            lg:border-solid lg:border-neutral-1000` : '';

        // Filtrăm opțiunile dacă există doar două și una este placeholder
        const filteredOptions = options.length === 2 ? options.filter((option) => !option.isPlaceholder) : options;
        return (
            <ul
                className={ `absolute z-40 top-full inset-s-0 border-b border-r border-solid border-transparent ml-0
                    overflow-y-auto transition-[200ms] duration-[max-height] will-change-[max-height] max-h-0
                    transition-shadow transition-colors duration-200 ease-in-out
                    ${classIsExpanded} ${this.sizeClass[size]}` }
                role="menu"
            >
                { filteredOptions.map(this.renderOption) }
            </ul>
        );
    }

    render() {
        const {
            attr: { id = '' } = {},
            isExpanded,
            handleSelectExpand,
            handleSelectListKeyPress,
            handleSelectExpandedExpand,
            options,
            size,
            isDisabled
        } = this.props;

        const hasOneOption = options.length === 2 && options[0].isPlaceholder;
        const classIsDisabled = isDisabled ? 'opacity-30' : '';
        const chevron = hasOneOption ? '' : (
                 <ChevronIcon
                     direction={ isExpanded && !isMobile.any() ? TOP : BOTTOM }
                     size={ M_KEY }
                     color={ BLACK_KEY }
                     className={ `absolute inset-e-3 inline-block pointer-events-none ${classIsDisabled}` }
                 />
        );

        return (
            <ClickOutside onClick={ handleSelectExpandedExpand }>
                <div
                    id={ `${id}_wrapper` }
                    block="FieldSelect"
                    onClick={ hasOneOption ? false : handleSelectExpand }
                    onKeyPress={ hasOneOption ? false : handleSelectListKeyPress }
                    role="button"
                    tabIndex="0"
                    aria-label="Select dropdown"
                    aria-expanded={ isExpanded }
                >
                    <div
                        className={ `flex items-center group ${this.sizeClass[size]}` }
                    >
                        { this.renderNativeSelect() }
                        { chevron }
                    </div>
                    { this.renderOptions() }
                </div>
            </ClickOutside>
        );
    }
}

export default FieldSelectComponent;
